import { FaroTable } from "@components/common/faro-table/faro-table";
import { FaroTableColumnVisibilityMenu } from "@components/common/faro-table/faro-table-column-visibility-menu";
import {
  MemberBulkButtonComponents,
  MemberTableSubject,
  TeamMemberBulkButtonComponents,
} from "@components/common/faro-table/faro-table-types";
import { useMembersColumns } from "@components/table/members/members-column/use-members-columns";
import { MEMBERS_SORT_BY_MAP } from "@components/table/members/members-sort-constants";
import { MEMBERS_TABLE_COLUMN_LABELS } from "@components/table/members/members-table-constants";
import {
  getMemberTableTrackingSubject,
  getMembersSortByFromGridSortModel,
} from "@components/table/members/members-table-utils";
import { BaseCompanyIdProps } from "@custom-types/base-types";
import {
  BaseMembersProps,
  MembersSortBy,
  SdbMemberTypes,
} from "@custom-types/member-types";
import { SdbProject } from "@custom-types/project-types";
import { useHandleColumnOrderChange } from "@hooks/use-handle-column-order-change";
import { SxProps, Theme } from "@mui/material";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { useAppSelector } from "@store/store-helper";
import { tableTypeSelector } from "@store/table/table-selector";
import { TableType } from "@utils/track-event/track-event-list";
import { ReactNode, useCallback } from "react";
import { useSetMemberTableType } from "@hooks/table/use-set-member-table-type";
import { GridSortModel } from "@mui/x-data-grid-pro";

interface Props extends Partial<BaseCompanyIdProps>, BaseMembersProps {
  /** The selected project if there is one */
  project?: SdbProject | null;

  /** Recognizes on which context the table is being used */
  tableSubject: MemberTableSubject;

  /** Whether the data for showing a table is loading or not */
  isLoading: boolean;

  /** The selected group if there is one */
  group?: FaroCoreApiTypes.GroupDetailResponse;

  /** List of all the bulk actions and callbacks */
  bulkActionButtons?:
    | MemberBulkButtonComponents
    | TeamMemberBulkButtonComponents;

  /** The selected team if there is one */
  team?: FaroCoreApiTypes.Team;

  /** Whether to hide the checkbox on each row or not */
  shouldHideCheckbox?: boolean;

  /** The component that will render for the side panel  */
  sidePanelContent?: ReactNode;

  /** The size of the side panel  */
  sidePanelContentWidth?: string;

  /** The status of the side panel  */
  isSidePanelOpened?: boolean;

  /** Callback fired when a row is clicked. */
  onRowClick?: (
    member: SdbMemberTypes | FaroCoreApiTypes.UserAsManager | null
  ) => void;

  /** Current sort by value from the sort dropdown */
  sortBy: MembersSortBy;

  /** Callback to update the sort by value in the sort dropdown */
  setSortBy: (sortBy: MembersSortBy) => void;

  /** Customize css */
  sx?: SxProps<Theme>;
}

/** Listing all the members of a subject such as a company or a project in a table */
export function MembersTable({
  companyId,
  members,
  project,
  isLoading,
  tableSubject,
  group,
  bulkActionButtons,
  team,
  shouldHideCheckbox,
  sidePanelContent,
  sidePanelContentWidth,
  isSidePanelOpened,
  onRowClick,
  sortBy,
  setSortBy,
  sx,
}: Props): JSX.Element {
  const tableType = useAppSelector(tableTypeSelector);

  const columns = useMembersColumns({
    companyId: companyId ?? "",
    project,
    tableSubject,
    group,
    team,
  });

  const handleColumnOrderChange = useHandleColumnOrderChange(
    tableType ?? TableType.companyMembers
  );

  useSetMemberTableType(tableSubject);

  /**
   * Convert the sortBy prop to a GridSortModel for the table UI.
   */
  const sortModel = [MEMBERS_SORT_BY_MAP[sortBy]];

  // Callback fired when the sort option changes
  const handleSortModelChange = useCallback(
    (newModel: GridSortModel) => {
      const newSortBy = getMembersSortByFromGridSortModel(newModel);

      // Sync the sort dropdown with the table sort
      setSortBy?.(newSortBy);
    },
    [setSortBy]
  );

  return (
    <FaroTable
      entities={members}
      uniqueIdKey={"identity"}
      isLoading={isLoading}
      columns={columns}
      bulkActionButtons={bulkActionButtons}
      shouldHideCheckbox={shouldHideCheckbox}
      sidePanelContent={sidePanelContent}
      sidePanelContentWidth={sidePanelContentWidth}
      isSidePanelOpened={isSidePanelOpened}
      tableType={tableType ?? TableType.companyMembers}
      onRowClick={onRowClick}
      sortModel={sortModel}
      onSortModelChange={handleSortModelChange}
      sortTrackingSubject={getMemberTableTrackingSubject(tableSubject)}
      filterMenuTrigger={
        <FaroTableColumnVisibilityMenu
          labelsMap={MEMBERS_TABLE_COLUMN_LABELS}
          disabledColumns={["user", "name"]}
          tableType={tableType ?? TableType.companyMembers}
        />
      }
      onColumnOrderChange={handleColumnOrderChange}
      sx={{
        ...sx,
        "& .MuiDataGrid-columnHeader:hover .drag-handle": {
          display: "flex",
        },

        /** Fill all the header */
        "& .MuiDataGrid-columnHeaderTitleContainerContent": {
          width: "100%",
        },
      }}
    />
  );
}
